import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";

import { Grid } from "@mui/material";
import classNames from "classnames";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams, Prompt } from "react-router-dom";
import { v4 as uuid } from "uuid";
import * as yup from "yup";

import { PRIVATE_ROUTES } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import useYupValidation, {
  useYupSchemas,
  useHandleValidate,
} from "../../../hooks/useYupValidation";
import { MarkNumbers } from "../../../models/markNumbers";
import { PurchaseOrder } from "../../../models/purchase.order";
import { ShipmentDocument } from "../../../models/shipment.document";
import { capitalizeFirstLetterAllWorlds } from "../../../util/capitalizeFirstLetter";
import {
  BookingStatus,
  CommercialInvoice,
  CountryCode,
  ResourceName,
  ShipmentDetails,
  ShipmentType,
  UserTypes,
} from "../../../util/enums";
import BadgeBookingStatus from "../../Generic/BadgeBookingStatus";
import Breadcrumbs from "../../Generic/Breadcrump";
import CartonSSCC from "../../Generic/CartonSSCC";
import { CartonSSCCFrom } from "../../Generic/CartonSSCC/util";
import { MenuActionsButtonItems } from "../../Generic/MenuActionsButton";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import UITabMaterial, { PanelProps } from "../../Generic/UITabMaterial";
import Documents from "../../Shipments/Maintenance/Documents";
import { SkeletonProducts } from "../../Shipments/Maintenance/Products";
import TitlePage from "../../TitlePage";
import useActionBooking from "../actions";
import { BookingResourcesProvider, useBookingResources } from "../context/AvailableOrders";
import { BookingProvider, useBookingContext } from "../context/Booking";
import { ShipmentResourcesProvider } from "../context/Resources";
import { useBookingFiles } from "../hooks/useBookingFiles";
import { useDocuments } from "../hooks/useDocuments";
import { useBookingResourcesContext } from "../hooks/useResourcesContext";
import UnderReviewBookingModal, {
  ReviewBookingConfirmationModalHandles,
} from "../UnderReviewBookingModal";
import { BookingObjects, BookingTabs, isBookingInCFSProcess } from "../util";
import { convertPurchaseOrdersToPurchaseOrdersProducts } from "./actions";
import BookingConfirmationModal, {
  BookingConfirmationModalHandles,
} from "./BookingConfirmationModal";
import CbmWeight from "./CbmWeight";
import { CommercialInvoiceDetails } from "./CommercialInvoice";
import BookingActionsButtons from "./components/BookingsActionsButtons";
import ConfirmBookModal, { ConfirmBookModalHandles } from "./ConfirmBookModal";
import { Details } from "./Details";
import SkeletonDetails from "./Details/skeleton";
import MarkAndNumbers from "./MarkAndNumbers";
import BookingOrders from "./Orders";
import ReceivedByCFSModal, { ReceivedByCfsConfirmationModalHandles } from "./ReceivedByCFSModal";
import useStyles from "./styles";
import { BookingSummary } from "./Summary";
import {
  calculateBookingCartonTotals,
  calculatePurchaseOrderTotal,
  calculateScannedItemsFromBooking,
  isBookingAvailableToGenerateDocument,
  getSSCCPrerequisiteTranslationKeys,
} from "./util";
import { checkResourceCan, FN_NAMES, hasPermission } from "Util/permissions";
import { ResourceCanEnum } from "Util/enums/accessRule";

interface BookingMaintenanceProps {}

interface BookingMaintenanceRouteParams {
  bookingId?: string;
}

interface BookingMaintenanceLocationParams {
  viewOnly?: boolean;
}

const BookingMaintenanceScreen: React.FC<BookingMaintenanceProps> = () => {
  const { bookingId } = useParams<BookingMaintenanceRouteParams>();
  const { state } = useLocation<BookingMaintenanceLocationParams>();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const { currentUser, isSupplierPortal, isFMVersion, isFMorSPVersion, userPermissions } =
    useAppState();
  const classes = useStyles();
  const { isLoading: loadingAvailableOrders, isExporterInfoDifferentFromVendor } =
    useBookingResources();
  const [viewOnly, setViewOnly] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [disableSave, setDisableSave] = useState<boolean>(false);
  const {
    portList,
    countryList,
    companyDocuments,
    loading: loadingResources,
    resourceCompanyId,
  } = useBookingResourcesContext();
  const [leftPanelSelected, setLeftPanelSelected] = useState<string>(BookingTabs.DETAILS);

  const confirmBookModalRef = useRef<ConfirmBookModalHandles>(null);
  const underReviewModalRef = useRef<ReviewBookingConfirmationModalHandles>(null);
  const completeBookModalRef = useRef<BookingConfirmationModalHandles>(null);
  const receiveByCfsModalRef = useRef<ReceivedByCfsConfirmationModalHandles>(null);
  const reviewByCfsModalRef = useRef<BookingConfirmationModalHandles>(null);
  const isWarehouseUser = currentUser?.typeUser === UserTypes.WAREHOUSE;
  const [bookingPermissions, setBookingPermissions] = useState<{
    edit: boolean;
    create: boolean;
  }>({
    edit: false,
    create: false,
  });

  const requestUuid = useMemo(() => uuid(), []);

  const {
    booking,
    setBooking,
    handleUpdateBooking,
    bookingAlreadySubmitted,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    handleChangeOptions,
    cartonLogs,
    changedOptions,
    responseError,
    setResponseError,
  } = useBookingContext();

  const onChangeDocuments = (documentsClone: ShipmentDocument[]) => {
    setBooking((prev) => ({ ...prev, documents: documentsClone }));
  };
  const { documentsJoinned } = useDocuments({ documents: booking?.documents });
  const {
    onDownloadDocument,
    onDownloadDocumentCsv,
    onDownloadAllDocuments,
    onUploadDocument,
    saveShipmentDocuments,
    loadingSavingCsvDocument,
    loadingUpdateShipmentDocuments,
    loadingAllDocuments,
  } = useBookingFiles({
    bookingId: booking?.id,
    hasBookingId: !!booking?.id,
    documents: booking?.documents,
    onChangeDocuments,
    setHasUnsavedChanges,
  });

  const isLoadingPortFromBangladesh = useMemo(() => {
    let countryCode;

    if (isFMorSPVersion) countryCode = booking?.portOfLoading?.country?.code;
    else
      countryCode =
        portList.find((port) => port.id === booking?.portOfLoadingId)?.country?.code ??
        "countryCode";

    return countryCode === CountryCode.BANGLADESH;
  }, [booking?.portOfLoadingId, portList, viewOnly]);

  const isDischargePortFromBrazil = useMemo(() => {
    let countryCode;

    if (isFMorSPVersion) countryCode = booking?.portOfDischarge?.country?.code;
    else
      countryCode =
        portList.find((port) => port.id === booking?.portOfDischargeId)?.country?.code ??
        "countryCode";

    return countryCode === CountryCode.BRAZIL;
  }, [booking?.portOfDischargeId, portList, viewOnly]);

  const ORIGIN_CRITERION_ORIGIN_COUNTRIES = [
    CountryCode.BANGLADESH,
    CountryCode.CAMBODIA,
    CountryCode.INDONESIA,
    CountryCode.PAKISTAN,
  ];

  const ssccPrerequisiteMessages = useMemo(
    () =>
      getSSCCPrerequisiteTranslationKeys({
        booking,
        commercialInvoice: booking?.commercialInvoice,
        isLoadingPortFromBangladesh,
        isExporterInfoDifferentFromVendor,
      }),
    [
      booking,
      booking?.commercialInvoice,
      isLoadingPortFromBangladesh,
      isExporterInfoDifferentFromVendor,
    ]
  );

  const showOriginCriterion = useMemo(() => {
    const ciCountry = countryList?.find((c) => c.id === booking?.commercialInvoice?.countryId);
    const hasQualifyingOrigin =
      !!ciCountry?.code &&
      ORIGIN_CRITERION_ORIGIN_COUNTRIES.includes(ciCountry.code as CountryCode);

    const destinationCode =
      booking?.portOfDestination?.country?.code ??
      booking?.portOfDischarge?.country?.code ??
      portList.find((port) => port.id === booking?.portOfDestinationId)?.country?.code ??
      portList.find((port) => port.id === booking?.portOfDischargeId)?.country?.code;

    return !!hasQualifyingOrigin && destinationCode === CountryCode.UNITED_KINGDOM;
  }, [booking?.commercialInvoice?.countryId, booking?.portOfDestinationId, booking?.portOfDestination, booking?.portOfDischargeId, booking?.portOfDischarge, portList, countryList]);

  const {
    getBookingById,
    useSaveBooking,
    getCartonsByBooking,
    getOrdersByBooking,
    useSendCartonLogs,
    useSendBookingReviewCfs,
    requestingSaveBooking,
  } = useActionBooking();

  const { data: bookingData, isInitialLoading: loadingBooking } = getBookingById(Number(bookingId));
  const { data: cartonsBookingData, isInitialLoading: loadingCartons } = getCartonsByBooking(
    Number(bookingId)
  );
  const { data: ordersBookingData, isInitialLoading: loadingOrders } = getOrdersByBooking(
    Number(bookingId)
  );

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setBookingPermissions({
        edit:
          checkResourceCan(userPermissions, ResourceName.BOOKING, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_BOOKING_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.BOOKING, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_BOOKING_ADD_NEW),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (bookingData) {
      setBooking((prev) => ({
        ...prev,
        ...bookingData,
      }));
    }
  }, [bookingData]);

  useEffect(() => {
    if (cartonsBookingData) {
      setBooking((prev) => ({
        ...prev,
        ...calculateBookingCartonTotals(cartonsBookingData),
      }));
    }
  }, [cartonsBookingData]);

  useEffect(() => {
    if (ordersBookingData && cartonsBookingData) {
      setBooking((prev) => ({
        ...prev,
        purchaseOrders: calculatePurchaseOrderTotal(ordersBookingData, cartonsBookingData),
      }));
    }
  }, [ordersBookingData, cartonsBookingData]);

  const {
    mutate: saveBooking,
    isLoading: loadingSaveBooking,
    isPaused: pausedSaveBooking,
  } = useSaveBooking(() => {
    setHasUnsavedChanges(false);
  });

  const { mutate: sendBookingReviewCfs, isLoading: loadingReviewByCfs } = useSendBookingReviewCfs();

  const { mutate: sendCartonLogs } = useSendCartonLogs();

  const { fieldHasError, getErrorByField, someFieldHasError } = useYupValidation(errors);
  const {
    stringRequired,
    stringNullable,
    numberRequired,
    numberNullable,
    objectRequired,
    objectNullable,
  } = useYupSchemas(translate);

  useEffect(() => {
    if (state && state.viewOnly) setViewOnly(state.viewOnly);
  }, [state]);

  useEffect(() => {
    if (viewOnly === false && !bookingId) {
      onUpdateBooking(ShipmentDetails.CONTAINER_ID, Math.floor(Math.random() * 100000000), true);
    }
  }, [viewOnly, bookingId]);

  const isSeaOrAirMode = useMemo(
    () =>
      (booking?.shipmentType === ShipmentType.AIR || booking?.shipmentType === ShipmentType.SEA) ??
      false,
    [booking?.shipmentType]
  );

  const resourceOrders = useMemo(() => booking?.purchaseOrders || [], [booking?.purchaseOrders]);
  const resourceProducts = useMemo(
    () => convertPurchaseOrdersToPurchaseOrdersProducts(booking?.purchaseOrders || []),
    [booking?.purchaseOrders]
  );

  const allCartonsAlreadyReceived = useMemo(
    () => !booking?.cartons?.find((carton) => !carton.isReceivedByCfs),
    [booking?.cartons]
  );

  const numberRequiredFromSeaAirMode = numberNullable.when("isSeaOrAirMode", {
    is: true,
    then: numberRequired,
  });

  const stringRequiredFromBangladesh = stringNullable.when("isLoadingPortFromBangladesh", {
    is: true,
    then: stringRequired,
  });

  const stringRequiredExporterInfoDifferentFromVendor = stringNullable.when(
    "isExporterInfoDifferentFromVendor",
    {
      is: true,
      then: stringRequired,
    }
  );

  const schema = yup.object().shape({
    shipmentType: stringRequired,
    statusShipment: stringRequired,
    cargoReadyDate: stringRequired,
    portOfLoadingId: numberRequiredFromSeaAirMode,
    portOfDischargeId: numberRequiredFromSeaAirMode,
    portOfReceiptId: numberRequiredFromSeaAirMode,
    portOfDestinationId: numberRequiredFromSeaAirMode,
    consigneeId: numberRequired,
    containerId: numberRequired,
    officeId: numberRequired,
    shipTerms: stringRequired,

    // Commercial invoice fields
    beneficiaryName: stringRequired,
    beneficiaryAddress: objectRequired,
    fiscalAgentDetails: stringRequired,
    salesContractNumber: stringRequired,
    salesContractDate: stringRequired,
    expNumber: stringRequired,
    expDate: stringRequired,
    paymentTerms: stringRequired,
    countryId: stringRequired,
    vendorId: stringRequired,

    // only if the exporter information is different from the vendor
    exporterName: stringRequiredExporterInfoDifferentFromVendor,
    exporterContactDetails: stringRequiredExporterInfoDifferentFromVendor,

    // Bangladesh fields
    bankName: stringRequiredFromBangladesh,
    bankAddress: objectNullable.when("isLoadingPortFromBangladesh", {
      is: true,
      then: objectRequired,
    }),
    accountNumber: stringRequiredFromBangladesh,
    swiftCode: stringRequiredFromBangladesh,
    termsOfDelivery: stringRequiredFromBangladesh,
    udNumber: stringRequiredFromBangladesh,
    ercNumber: stringRequiredFromBangladesh,
    rexNumber: stringRequiredFromBangladesh,
    markAndNumbers: yup.array().test("markAndNumbers", () => validateMarks()),
    containers: yup
      .array()
      .of(
        yup.object().shape({
          container: yup.string().test("container", "This field is required", (value) => {
            if (booking?.shipTerms === "CY/CY" && isSupplierPortal && !value?.trim().length) {
              return false;
            }

            return true;
          }),
          size: yup.string().test("size", "This field is required", (value) => {
            if (booking?.shipTerms === "CY/CY" && isSupplierPortal && !value?.trim().length) {
              return false;
            }

            return true;
          }),
        })
      )
      .test("containers", "This field is required", (value) => {
        if (booking?.shipTerms === "CY/CY" && isSupplierPortal && !value?.length) {
          return false;
        }

        return true;
      }),
  });

  const handleValidate = () => {
    setDisableSave(true);

    const validated = useHandleValidate(
      {
        isSeaOrAirMode,
        isLoadingPortFromBangladesh,
        isExporterInfoDifferentFromVendor,
        shipmentType: booking?.shipmentType,
        statusShipment: booking?.statusShipment,
        cargoReadyDate: booking?.cargoReadyDate,
        containerId: booking?.containerId,
        consigneeId: booking?.consigneeId,
        portOfLoadingId: booking?.portOfLoadingId,
        portOfDischargeId: booking?.portOfDischargeId,
        portOfReceiptId: booking?.portOfReceiptId,
        portOfDestinationId: booking?.portOfDestinationId,
        officeId: booking?.officeId,
        shipTerms: booking?.shipTerms,
        containers: booking?.containers || [],
        ...booking?.commercialInvoice,
      },
      schema,
      setErrors
    );

    return validated;
  };

  const validateMarks = () => {
    if (!booking?.marks?.length) {
      NotificationToast.error(translate("markAndNumbersShouldHaveAtLeastOneFilledLine"));

      return false;
    }

    for (let i = 0; i <= booking.marks.length; i += 1) {
      if (booking.marks[i]?.description?.trim()?.length && booking.marks[i]?.mark?.trim()?.length) {
        return true;
      }
    }
    NotificationToast.error(translate("markAndNumbersShouldHaveAtLeastOneFilledLine"));

    return false;
  };

  const validateAndSaveBooking = (saveAndBook?: boolean, complete?: boolean) => {
    if (!booking) {
      setDisableSave(false);

      return;
    }

    if (!isBookingInCFSProcess(booking) && !handleValidate()) {
      setDisableSave(false);

      return;
    }

    if (loadingSaveBooking || pausedSaveBooking) return;

    saveBooking(
      {
        id: booking?.id,
        booking: { ...booking, projectId: 23 },
        saveAndBook,
        received: complete,
        uuid: booking?.uuid || requestUuid,
      },
      {
        onSettled: () => setDisableSave(false),
      }
    );
    sendCartonLogs(cartonLogs);
    saveShipmentDocuments();
  };

  const onCancel = () => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace(PRIVATE_ROUTES.BOOKINGS);
    }
  };

  const onUpdateBooking = (attribute: string, value: any, noChanges?: boolean) => {
    if (!noChanges) setHasUnsavedChanges(true);
    handleUpdateBooking(attribute, value);
  };

  const onConfirmSaveAndBook = () => {
    validateAndSaveBooking(true);
  };

  const onConfirmCompleteBook = () => {
    validateAndSaveBooking(undefined, true);
  };

  const onConfirmUnderReviewBook = (_id?: number) => {
    if (!booking) return;

    saveBooking({
      id: booking?.id,
      booking: { ...booking, statusShipment: BookingStatus.UNDER_REVIEW, projectId: 23 },
      overrideChangedOptions: { ...changedOptions, booking: true },
    });
  };

  const onConfirmReceiveByCfsBook = (_id?: number) => {
    if (!booking) return;

    saveBooking({
      id: booking?.id,
      booking: { ...booking, projectId: 23 },
      cfsReceived: true,
    });
  };

  const onConfirmReviewByCfsBook = (_id?: number) => {
    if (!booking || !booking.id) return;

    if (loadingReviewByCfs) return;

    sendBookingReviewCfs(booking.id, {
      onSuccess: () => {
        history.push(PRIVATE_ROUTES.BOOKINGS);
      },
    });
  };

  const onSaveAndBook = () => {
    if (!handleValidate()) {
      setDisableSave(false);

      return;
    }

    confirmBookModalRef.current?.open();
    setDisableSave(false);
  };

  const onCompleteBook = () => {
    completeBookModalRef.current?.open(translate("received"));
  };

  const onUnderReviewBook = () => {
    if (booking?.id) underReviewModalRef.current?.open(booking?.id);
  };

  const onReceiveByCfsBook = () => {
    if (!allCartonsAlreadyReceived) return;

    receiveByCfsModalRef.current?.open();
  };

  const onReviewByCfsBook = () => {
    reviewByCfsModalRef.current?.open(translate("cfsReview"));
  };

  const menuItems: MenuActionsButtonItems[] = isWarehouseUser
    ? [
        {
          title: `${translate("markBookingAs")} "${translate("receivedByCFS")}"`,
          action: onReceiveByCfsBook,
          disabled:
            !allCartonsAlreadyReceived ||
            booking?.statusShipment === BookingStatus.CFS_RECEIVED ||
            booking?.statusShipment === BookingStatus.CFS_REVIEW,
        },
        {
          title: `${translate("markBookingAs")} "${translate("cfsReview")}"`,
          action: onReviewByCfsBook,
          disabled: booking?.statusShipment !== BookingStatus.CONFIRMED,
        },
      ]
    : [
        {
          title: `${translate("markBookingAs")} "${translate("received")}"`,
          action: onCompleteBook,
        },
        {
          title: `${translate("markBookingAs")} "${translate("underReview")}"`,
          action: onUnderReviewBook,
        },
      ];

  useEffect(() => {
    if (!isLoadingPortFromBangladesh && booking?.commercialInvoice) {
      onUpdateBooking(
        "commercialInvoice",
        {
          ...booking?.commercialInvoice,
          bankName: undefined,
          bankAddress: undefined,
          accountNumber: undefined,
          swiftCode: undefined,
          termsOfDelivery: undefined,
          udNumber: undefined,
          ercNumber: undefined,
          rexNumber: undefined,
        },
        true
      );
    }
  }, [isLoadingPortFromBangladesh]);

  useEffect(() => {
    if (!isExporterInfoDifferentFromVendor && booking?.commercialInvoice) {
      onUpdateBooking(
        "commercialInvoice",
        {
          ...booking?.commercialInvoice,
          exporterName: null,
          exporterContactDetails: null,
        },
        true
      );
    }
  }, [isExporterInfoDifferentFromVendor]);

  const isUpdatingAction = useMemo(
    () =>
      disableSave ||
      loadingSaveBooking ||
      loadingSavingCsvDocument ||
      loadingBooking ||
      loadingOrders ||
      loadingCartons ||
      requestingSaveBooking,
    [
      disableSave,
      loadingSaveBooking,
      loadingSavingCsvDocument,
      loadingBooking,
      loadingOrders,
      loadingCartons,
      requestingSaveBooking,
    ]
  );

  const detailsPanel: PanelProps = {
    key: BookingTabs.DETAILS,
    menuItem: translate("details"),
    className: classes.uiTab,
    loading: loadingBooking,
    skeleton: SkeletonDetails,
    hasError: someFieldHasError(Object.values(ShipmentDetails)),
    render: () => (
      <Details
        data={booking}
        onUpdateShipment={onUpdateBooking}
        onUpdateAllShipment={(value) => {
          onUpdateBooking("all", {
            ...booking,
            ...value,
          });
        }}
        viewOnly={viewOnly || bookingAlreadySubmitted}
        errors={errors}
      />
    ),
  };

  const handleRemovePurchaseOrder = useCallback(
    (orderToRemove: PurchaseOrder) => {
      const ordersUpdated =
        booking?.purchaseOrders?.filter((order) => order.id !== orderToRemove.id) ?? [];
      const cartonsUpdated =
        booking?.cartons?.filter((carton) => carton.purchaseOrderId !== orderToRemove.id) ?? [];

      onUpdateBooking("purchaseOrders", ordersUpdated);
      handleChangeOptions(BookingObjects.PURCHASE_ORDERS);

      onUpdateBooking("cartons", cartonsUpdated);
      handleChangeOptions(BookingObjects.CARTONS);
    },
    [booking?.purchaseOrders, booking?.cartons]
  );

  const ordersPanel: PanelProps = {
    key: BookingTabs.ORDERS,
    menuItem: translate("orders"),
    className: classes.uiTab,
    loading: loadingOrders || loadingCartons,
    "data-cy": "tab-purchaseOrders",
    skeleton: () => <SkeletonProducts />,
    render: () => (
      <BookingOrders
        purchaseOrders={booking?.purchaseOrders || []}
        onAddPurchaseOrder={(orders) => {
          onUpdateBooking("purchaseOrders", orders);
          handleChangeOptions(BookingObjects.PURCHASE_ORDERS);
        }}
        onRemovePurchaseOrder={handleRemovePurchaseOrder}
        viewOnly={{
          fields: viewOnly || isFMVersion,
          actions: viewOnly || (!isFMVersion && bookingAlreadySubmitted),
        }}
        loadingOrders={loadingOrders}
        fromMaintenance={true}
      />
    ),
  };

  const ssccPanel: PanelProps = {
    menuItem: translate("sscc"),
    className: classes.uiTab,
    key: BookingTabs.SSCC,
    loading: loadingBooking || loadingOrders || loadingCartons,
    skeleton: () => <SkeletonProducts />,
    render: () => (
      <CartonSSCC
        cartons={booking?.cartons || []}
        updateCartons={(cartons) => {
          console.log(cartons);
          onUpdateBooking("cartons", cartons);
          handleChangeOptions(BookingObjects.CARTONS);
        }}
        onDeleteSSCC={(ssccs?: string[]) => {
          const cartons = cloneDeep(booking?.cartons);

          if (ssccs && cartons) {
            for (const sscc of ssccs) {
              const index = cartons?.findIndex((carton) => carton.sscc === sscc);

              if (index > -1) cartons?.splice(index, 1);
            }
          }

          onUpdateBooking("cartons", cartons);
          handleChangeOptions(BookingObjects.CARTONS);
        }}
        loadingCartons={false}
        viewOnly={{
          fields: viewOnly,
          actions: viewOnly || (!isFMVersion && bookingAlreadySubmitted) || isUpdatingAction,
        }}
        viewSupplier={isSupplierPortal}
        calledFrom={CartonSSCCFrom.BOOKING}
        subtitle={{
          quantity: calculateScannedItemsFromBooking(booking),
          text: translate("scannedItems"),
        }}
        booking={booking}
        resources={{
          purchaseOrders: resourceOrders,
          products: resourceProducts,
          resourceCompanyId,
        }}
        loadingProducts={loadingAvailableOrders}
        isLengthMandatory={isDischargePortFromBrazil}
        showOriginCriterion={showOriginCriterion}
        ssccPrerequisiteMessages={ssccPrerequisiteMessages}
        statusBooking={booking?.statusShipment}
        onUploadCsvS3={(csvShipmentDocument) => onUploadDocument(csvShipmentDocument)}
      />
    ),
    "data-cy": "sscc-tab",
  };

  const handleSaveDocuments = () => {
    if (viewOnly) {
      saveShipmentDocuments();

      return;
    }

    validateAndSaveBooking();
  };

  const documentsPanel: PanelProps = {
    key: BookingTabs.DOCUMENTS,
    menuItem: translate("documents"),
    className: classes.uiTab,
    render: () => (
      <Documents
        companyDocuments={companyDocuments}
        loadingResources={loadingResources}
        shipmentDocuments={documentsJoinned}
        onUpdateShipment={(attribute, value) => {
          onUpdateBooking(attribute, value);
          handleChangeOptions(BookingObjects.DOCUMENTS);
        }}
        isBooking
        viewOnly={false}
        loadingShipment={loadingBooking}
        onDownloadDocument={
          isBookingAvailableToGenerateDocument(booking) ? onDownloadDocument : undefined
        }
        onDownloadDocumentCsv={onDownloadDocumentCsv}
        loadingAllDocuments={loadingAllDocuments}
        onDownloadAllDocuments={onDownloadAllDocuments}
        loadingUploadCsvDocument={loadingSavingCsvDocument}
      />
    ),
  };

  const leftPanels = [detailsPanel, ssccPanel];

  if (
    booking &&
    booking?.statusShipment !== BookingStatus.CANCELLED &&
    !loadingBooking &&
    currentUser?.typeUser !== UserTypes.WAREHOUSE
  ) {
    leftPanels.push(documentsPanel);
  }

  if (booking && currentUser?.typeUser !== UserTypes.WAREHOUSE) {
    leftPanels.splice(1, 0, ordersPanel);
    const handleRemovePurchaseOrder = useCallback(
      (markNumbers: MarkNumbers) => {
        const marksUpdated = booking?.marks?.filter((mark) => mark.line !== markNumbers.line) ?? [];

        onUpdateBooking("markAndNumbers", marksUpdated);
        handleChangeOptions(BookingObjects.MARK_AND_NUMBERS);
      },
      [booking.marks]
    );

    const markAndNumbersPanel: PanelProps = {
      key: BookingTabs.MARK_AND_NUMBERS,
      menuItem: translate("markAndNumbers"),
      className: classes.uiTab,
      "data-cy": "tab-markAndNumbers",
      skeleton: () => <SkeletonProducts />,
      hasError: fieldHasError("markAndNumbers"),
      render: () => (
        <MarkAndNumbers
          marks={booking?.marks || []}
          onAddMarkNumbers={(marks) => {
            onUpdateBooking("marks", marks);
            handleChangeOptions(BookingObjects.MARK_AND_NUMBERS);
          }}
          onRemoveMarkNumbers={handleRemovePurchaseOrder}
          viewOnly={{
            fields: viewOnly || isFMorSPVersion,
            actions: viewOnly || (!isFMVersion && bookingAlreadySubmitted),
          }}
          loading={loadingBooking}
        />
      ),
    };

    leftPanels.splice(1, 0, markAndNumbersPanel);
  }

  if (booking && currentUser?.typeUser !== UserTypes.WAREHOUSE) {
    const commercialInvoice: PanelProps = {
      key: BookingTabs.COMMERCIAL_INVOICE,
      menuItem: translate("commercialInvoice"),
      className: classes.uiTab,
      "data-cy": "tab-commercialInvoice",
      hasError:
        someFieldHasError(Object.values(CommercialInvoice)) ||
        (responseError && responseError?.field === CommercialInvoice.INVOICE_NUMBER),
      render: () => (
        <CommercialInvoiceDetails
          shipment={booking}
          updateShipment={(attribute, value, commercialInvoiceProperty) => {
            onUpdateBooking(attribute, value);
            handleChangeOptions(BookingObjects.COMMERCIAL_INVOICE);
            if (commercialInvoiceProperty === CommercialInvoice.INVOICE_NUMBER)
              setResponseError(undefined);
          }}
          cancelShipment={() => {}}
          viewOnly={{
            fields: viewOnly || bookingAlreadySubmitted || isFMorSPVersion,
            actions: viewOnly || (!isFMVersion && bookingAlreadySubmitted),
          }}
          loadingBooking={loadingBooking}
          isBangladesh={isLoadingPortFromBangladesh}
          fieldHasError={fieldHasError}
          getErrorByField={getErrorByField}
          errorResponse={responseError}
        />
      ),
    };

    leftPanels.splice(3, 0, commercialInvoice);
  }

  if (isFMVersion && booking) {
    const receivedCartons = useMemo(
      () => booking?.cartons?.filter((carton) => carton.isReceivedByCfs) || [],
      [booking?.cartons]
    );

    const cbmWeight: PanelProps = {
      key: BookingTabs.VOLUME_AND_WEIGHT,
      menuItem: translate(BookingTabs.VOLUME_AND_WEIGHT),
      className: classes.uiTab,
      render: () => (
        <CbmWeight
          booking={booking}
          cartons={receivedCartons}
          updateCartons={(cartons) => {
            onUpdateBooking("cartons", cartons);
            handleChangeOptions(BookingObjects.CARTONS);
          }}
          loading={loadingBooking}
          viewOnly={viewOnly}
        />
      ),
    };

    const summaryPanel: PanelProps = {
      key: "summary",
      menuItem: translate("summary"),
      className: classNames(classes.uiTab, classes.withoutPadding),
      render: () => (
        <BookingSummary booking={booking} loadingData={loadingBooking || loadingCartons} />
      ),
    };

    leftPanels.splice(5, 0, cbmWeight);
    leftPanels.push(summaryPanel);
  }

  const getSubtitle = () => {
    if (viewOnly) {
      return translate("viewBooking");
    }
    if (bookingId) {
      return translate("editBooking");
    }

    return translate("createBooking");
  };

  return (
    <div className={classes.container}>
      <TitlePage
        left={
          <Breadcrumbs
            items={[
              {
                title: translate("booking"),
                link: {
                  url: PRIVATE_ROUTES.BOOKINGS,
                },
              },
              {
                title: getSubtitle(),
                color: "#465869",
              },
            ]}
          />
        }
        right={
          <Grid item xs={12} sm={6} md={6} lg={6} className={classes.titleDataContainer}>
            {booking?.statusShipment && (
              <BadgeBookingStatus
                value={capitalizeFirstLetterAllWorlds(booking.statusShipment)}
                className={classes.titleStatusBadge}
              />
            )}
            <span className={classes.titleData}>
              {booking?.containerId ? ` ${translate("booking")} #${booking?.containerId}` : ""}
            </span>
          </Grid>
        }
      />
      <Prompt when={hasUnsavedChanges} message="" />
      <ConfirmBookModal
        ref={confirmBookModalRef}
        onConfirm={onConfirmSaveAndBook}
        isUpdate={!!booking?.id}
      />
      <BookingConfirmationModal
        ref={completeBookModalRef}
        onSubmit={onConfirmCompleteBook}
        loadingAction={loadingSaveBooking}
      />
      <UnderReviewBookingModal
        ref={underReviewModalRef}
        onSubmit={onConfirmUnderReviewBook}
        loadingAction={loadingSaveBooking}
      />

      <ReceivedByCFSModal
        ref={receiveByCfsModalRef}
        onSubmit={onConfirmReceiveByCfsBook}
        loadingAction={loadingSaveBooking}
      />
      <BookingConfirmationModal
        ref={reviewByCfsModalRef}
        onSubmit={onConfirmReviewByCfsBook}
        loadingAction={loadingReviewByCfs}
      />
      <div className={classes.tabs}>
        <UITabMaterial
          leftPanels={leftPanels}
          showRightPanel={false}
          showActivityButton={false}
          leftDataCy="left-booking-tabs"
          onChangeLeftPanel={(event, panel, key) => key && setLeftPanelSelected(key)}
          tabFooter={
            <BookingActionsButtons
              loading={loadingBooking || isUpdatingAction || loadingUpdateShipmentDocuments}
              leftPanelSelected={leftPanelSelected}
              onCancel={onCancel}
              onSaveAndBook={onSaveAndBook}
              validateAndSaveBooking={validateAndSaveBooking}
              viewOnly={viewOnly}
              handleSaveDocuments={handleSaveDocuments}
              menuItems={menuItems}
              isWarehouseUser={isWarehouseUser}
              bookingAlreadySubmitted={bookingAlreadySubmitted}
              bookingId={booking?.id}
              permission={bookingPermissions}
            />
          }
        />
      </div>
    </div>
  );
};

const BookingMaintenance = () => {
  return (
    <BookingProvider>
      <ShipmentResourcesProvider>
        <BookingResourcesProvider>
          <BookingMaintenanceScreen />
        </BookingResourcesProvider>
      </ShipmentResourcesProvider>
    </BookingProvider>
  );
};

export default BookingMaintenance;
